// Pure delta math for the two-cell geometry compare dialog (T30). Deliberately dependency-free (no
// React, no maplibre types, no other spatial module) so it unit-tests instantly under vitest with
// zero DOM/mocks. The haversine formula mirrors `useMeasureGesture.ts`'s (T28) implementation —
// duplicated intentionally rather than importing that hook file's React/analytics graph into this
// pure module.

import type { DecodedGeometry, SpatialGeomType, SpatialGeoJson } from '../decode/types';

export type SpatialCompareBbox = [number, number, number, number] | null; // [minLng,minLat,maxLng,maxLat]

export type SpatialCompareStats = {
  geomTypes: [SpatialGeomType, SpatialGeomType];
  pointCounts: [number, number];
  bboxA: SpatialCompareBbox;
  bboxB: SpatialCompareBbox;
  /** Intersection-over-union of the two geometries' bounding boxes (planar rectangle ratio — a
   *  lightweight "how much do the extents overlap" indicator, not a geodesic area). */
  bboxIoU: number;
  /** Great-circle distance between the two geometries' vertex-average centroids, in meters.
   *  `null` in planar mode (coordinates aren't real WGS84 degrees, so haversine is meaningless). */
  centroidDistanceMeters: number | null;
};

const EARTH_RADIUS_METERS = 6371000;

function haversineDistanceMeters([lng1, lat1]: [number, number], [lng2, lat2]: [number, number]): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_METERS * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Single-pass bbox + vertex-average "centroid" over a geometry's coordinates. The centroid is a
 *  lightweight comparative stat (mean of all vertices) — not an area-weighted GIS-grade centroid. */
function geometryExtent(g: SpatialGeoJson): { bbox: SpatialCompareBbox; centroid: [number, number] | null } {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  let sumLng = 0;
  let sumLat = 0;
  let count = 0;

  const visit = (lng: number, lat: number) => {
    if (lng < minLng) minLng = lng;
    if (lat < minLat) minLat = lat;
    if (lng > maxLng) maxLng = lng;
    if (lat > maxLat) maxLat = lat;
    sumLng += lng;
    sumLat += lat;
    count++;
  };
  const walk = (coords: unknown): void => {
    if (!Array.isArray(coords)) return;
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      visit(coords[0] as number, coords[1] as number);
      return;
    }
    for (const c of coords) walk(c);
  };
  const visitGeom = (geom: SpatialGeoJson): void => {
    if (geom.type === 'GeometryCollection') {
      for (const child of geom.geometries) visitGeom(child);
      return;
    }
    walk(geom.coordinates);
  };
  visitGeom(g);

  if (count === 0 || !Number.isFinite(minLng) || !Number.isFinite(minLat)) {
    return { bbox: null, centroid: null };
  }
  return { bbox: [minLng, minLat, maxLng, maxLat], centroid: [sumLng / count, sumLat / count] };
}

function bboxArea(b: [number, number, number, number]): number {
  return Math.max(0, b[2] - b[0]) * Math.max(0, b[3] - b[1]);
}

function bboxesEqual(a: [number, number, number, number], b: [number, number, number, number]): boolean {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3];
}

/** Intersection-over-union of two planar bboxes. Exact-match bboxes (incl. degenerate/point boxes,
 *  where union area is 0) return 1; a null side, or non-overlapping/non-identical boxes, return 0. */
function bboxIoU(a: SpatialCompareBbox, b: SpatialCompareBbox): number {
  if (!a || !b) return 0;
  if (bboxesEqual(a, b)) return 1;
  const minLng = Math.max(a[0], b[0]);
  const minLat = Math.max(a[1], b[1]);
  const maxLng = Math.min(a[2], b[2]);
  const maxLat = Math.min(a[3], b[3]);
  if (maxLng < minLng || maxLat < minLat) return 0;
  const interArea = bboxArea([minLng, minLat, maxLng, maxLat]);
  const unionArea = bboxArea(a) + bboxArea(b) - interArea;
  return unionArea > 0 ? interArea / unionArea : 0;
}

/** Delta math for the two-cell compare dialog. Pass `{ planar: true }` when the pair couldn't be
 *  resolved to real WGS84 degrees (T12 planar fallback) — `centroidDistanceMeters` is `null` then,
 *  since a haversine distance over raw/local coordinates would be meaningless. */
export function spatialCompareStats(
  a: DecodedGeometry,
  b: DecodedGeometry,
  opts: { planar?: boolean } = {},
): SpatialCompareStats {
  const extentA = geometryExtent(a.geojson);
  const extentB = geometryExtent(b.geojson);

  const centroidDistanceMeters =
    !opts.planar && extentA.centroid && extentB.centroid
      ? haversineDistanceMeters(extentA.centroid, extentB.centroid)
      : null;

  return {
    geomTypes: [a.geomType, b.geomType],
    pointCounts: [a.pointCount, b.pointCount],
    bboxA: extentA.bbox,
    bboxB: extentB.bbox,
    bboxIoU: bboxIoU(extentA.bbox, extentB.bbox),
    centroidDistanceMeters,
  };
}
